import React, { useState, useEffect, useCallback } from 'react';
import ConfirmationDialog from '../../settings/components/ConfirmationDialog';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Grid, Alert, Link,
  List, ListItem, ListItemText, Checkbox, FormGroup, FormControlLabel,
  Snackbar, IconButton
} from '@mui/material';
import { Delete as DeleteIcon,
  Save as SaveIcon, Cancel as CancelIcon, UploadFile as UploadFileIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon, Info as InfoIcon
} from '@mui/icons-material';
import { useLoading } from '../../context/LoadingContext';

// Define possible statuses - should match backend enum
const STATUS_OPTIONS = ['Not Started', 'Requested', 'Pending Review', 'Document Uploaded', 'Approved', 'Denied', 'Expired', 'Error'];
const CHECK_TYPE_OPTIONS = ['MVR', 'Criminal', 'Comprehensive', 'Other'];
const ROLE_OPTIONS = ['admin', 'dispatcher', 'answeringService', 'driver'];

// Initial state for a single location entry
const initialLocationState = { city: '', countyOrParish: '', state: '' };
// Initial state for address
const initialAddressState = { street1: '', street2: '', city: '', state: '', zip: '', country: 'US' };
// Initial state for assigned roles
const initialRolesState = { admin: false, dispatcher: false, answeringService: false, driver: false, other: false, otherRoleName: '', originalRole: '' };

// Helper function to map role from upload to checkbox roles
const mapRoleToCheckboxes = (role) => {
  if (!role) return { ...initialRolesState };

  const roleStr = role.toLowerCase().trim();
  const result = { ...initialRolesState, originalRole: role };

  // If role is "owner", select all roles (case-insensitive)
  if (roleStr === 'owner') {
    return {
      admin: true,
      dispatcher: true,
      answeringService: true,
      driver: true,
      other: false,
      otherRoleName: '',
      originalRole: role
    };
  }

  // Map standard roles (case-insensitive)
  if (roleStr === 'admin') result.admin = true;
  else if (roleStr === 'dispatcher') result.dispatcher = true;
  else if (roleStr === 'answeringservice') result.answeringService = true;
  else if (roleStr === 'driver') result.driver = true;
  // For non-standard roles, set as "other"
  else if (roleStr && !ROLE_OPTIONS.map(r => r.toLowerCase()).includes(roleStr)) {
    result.other = true;
    result.otherRoleName = role;
  }

  return result;
};

function EditBackgroundCheckDialog({ open, onClose, record, application, onSaveSuccess }) {
  const [formData, setFormData] = useState({});
  const [fileToUpload, setFileToUpload] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newLocation, setNewLocation] = useState(initialLocationState);
  const { startLoading } = useLoading();

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const getAuthHeader = useCallback((contentType = 'application/json') => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    return headers;
  }, []);

  useEffect(() => {
    if (record) {
      // Saved Assigned Roles selections take priority; the employeeRole
      // mapping is only a default for records with no role ever selected
      // (fresh uploads store an all-false assignedRoles subdocument, which
      // must not wipe the mapped defaults).
      const savedRoles = record.assignedRoles || {};
      const hasSavedSelection = ROLE_OPTIONS.some(r => savedRoles[r]) ||
          savedRoles.other || savedRoles.otherRoleName;
      const finalAssignedRoles = hasSavedSelection
          ? { ...initialRolesState, ...savedRoles }
          : mapRoleToCheckboxes(record.employeeRole);

      finalAssignedRoles.originalRole = record.employeeRole || '';

      setFormData({
        ...record,
        address: record.address ? { ...initialAddressState, ...record.address } : { ...initialAddressState },
        assignedRoles: finalAssignedRoles,
        criminalRecordLocations: Array.isArray(record.criminalRecordLocations) ? record.criminalRecordLocations : []
      });
    } else {
      setFormData({
        personName: '',
        email: '',
        phone: '',
        address: { ...initialAddressState },
        assignedRoles: { ...initialRolesState },
        dateOfBirth: '', ssnLast4: '', checkType: 'Comprehensive',
        status: 'Requested', dateRequested: new Date().toISOString().split('T')[0],
        dateCompleted: '', dateExpires: '', dateReviewed: '', reviewNotes: '',
        documentPath: '', provider: '', mvrStatus: '', criminalStatus: '',
        criminalRecordLocations: [],
        otherDetails: ''
      });
    }
    setFileToUpload(null);
    setSaveError(null);
    setNewLocation(initialLocationState);
  }, [open, record]); // Reset form when dialog opens or record changes

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name.startsWith('address.')) {
        const field = name.split('.')[1];
        setFormData(prev => ({
            ...prev,
            address: {
                ...(prev.address || initialAddressState),
                [field]: value
            }
        }));
    } else if (name.startsWith('assignedRoles.')) {
         const field = name.split('.')[1];
         const fieldValue = type === 'checkbox' ? checked : value;
         setFormData(prev => ({
             ...prev,
             assignedRoles: {
                 ...(prev.assignedRoles || initialRolesState),
                 [field]: fieldValue
             }
         }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (event) => {
    setFileToUpload(event.target.files[0]);
  };

  const handleNewLocationChange = (event) => {
    const { name, value } = event.target;
    setNewLocation(prev => ({ ...prev, [name]: value }));
  };

  const handleAddLocation = () => {
    if (newLocation.city || newLocation.countyOrParish || newLocation.state) {
      setFormData(prev => ({
        ...prev,
        criminalRecordLocations: [...(prev.criminalRecordLocations || []), { ...newLocation }]
      }));
      setNewLocation(initialLocationState);
    }
  };

  const handleRemoveLocation = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      criminalRecordLocations: (prev.criminalRecordLocations || []).filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSave = async () => {
    const stopLoading = startLoading();
    setSaveError(null);
    let endpoint = '';
    let method = '';
    const isAddingNew = !record?._id;

    const dataToSend = { ...formData };
    ['dateOfBirth', 'dateCompleted', 'dateExpires', 'dateReviewed', 'dateRequested'].forEach(field => {
        if (!dataToSend[field]) {
            dataToSend[field] = null;
        }
    });
     dataToSend.criminalRecordLocations = Array.isArray(dataToSend.criminalRecordLocations) ? dataToSend.criminalRecordLocations : [];
     dataToSend.address = dataToSend.address || initialAddressState;
     dataToSend.assignedRoles = dataToSend.assignedRoles || initialRolesState;


    let uploadedFilePath = formData.documentPath;
    if (fileToUpload) {
        const fileFormData = new FormData();
        fileFormData.append('backgroundCheckDoc', fileToUpload);
        fileFormData.append('applicationId', application._id);
        fileFormData.append('companyName', application.companyName);
        fileFormData.append('personName', dataToSend.personName);

        try {
            const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/upload-background-check`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: fileFormData,
            });
            if (!uploadResponse.ok) {
                 const errorText = await uploadResponse.text();
                 console.error("Upload response error text:", errorText);
                 throw new Error(`File upload failed: ${uploadResponse.statusText}`);
            }
            const uploadResult = await uploadResponse.json();
            uploadedFilePath = uploadResult.filePath;
            dataToSend.documentPath = uploadedFilePath;
            showSnackbar('File uploaded successfully!', 'success');
        } catch (err) {
            console.error("File upload error:", err);
            setSaveError(err.message || 'File upload failed.');
            stopLoading();
            return;
        }
    }


    if (isAddingNew) {
      endpoint = `${process.env.REACT_APP_API_URL}/api/applications/${application._id}/background-checks`;
      method = 'POST';
    } else {
      endpoint = `${process.env.REACT_APP_API_URL}/api/background-checks/${record._id}`;
      method = 'PUT';
    }

    try {
      const response = await fetch(endpoint, {
        method: method,
        headers: getAuthHeader(),
        body: JSON.stringify(dataToSend)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${isAddingNew ? 'add' : 'save'} record.`);
      }
      const result = await response.json();
      showSnackbar(`Record ${isAddingNew ? 'added' : 'saved'} successfully!`, 'success');
      onSaveSuccess(result); // Call the success callback
      onClose(); // Close the dialog on success
    } catch (err) {
      console.error(`Error ${isAddingNew ? 'adding' : 'saving'} background check:`, err);
      setSaveError(err.message || `Could not ${isAddingNew ? 'add' : 'save'} record.`);
      showSnackbar(`Failed to ${isAddingNew ? 'add' : 'save'} record: ${err.message}`, 'error');
    } finally {
      stopLoading();
    }
  };

  const handleDeleteClick = () => {
    if (!record?._id) return;
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const stopLoading = startLoading();
    setSaveError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/background-checks/${record._id}`, {
        method: 'DELETE',
        headers: getAuthHeader(null)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete record.');
      }
      showSnackbar('Record deleted successfully!', 'success');
      onSaveSuccess(null, record._id); // Indicate deletion
      onClose(); // Close the dialog on success
    } catch (err) {
      console.error("Error deleting background check:", err);
      setSaveError(err.message || 'Could not delete record.');
      showSnackbar(`Failed to delete record: ${err.message}`, 'error');
    } finally {
      stopLoading();
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Function to create the full document URL (adapted from BackgroundCheckTab)
  const getDocumentUrl = (filePath) => {
    if (!filePath) return null;
    const baseUrl = process.env.NODE_ENV === 'production' ? '' : process.env.REACT_APP_API_URL;

    // Normalize path separators to forward slashes for consistent processing
    const normalizedPath = filePath.replace(/\\/g, '/');
    const pathParts = normalizedPath.replace(/^uploads\//, '').split('/');

    // These files are auth-gated server-side; links can't send headers, so
    // the JWT rides along as a query param.
    const token = localStorage.getItem('token');
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';

    if (pathParts.length >= 4) {
      const companyName = encodeURIComponent(pathParts[0]);
      const employeeName = encodeURIComponent(pathParts[2]);
      const fileName = encodeURIComponent(pathParts[3]);

      return `${baseUrl}/api/applications/background-check-file/${companyName}/${employeeName}/${fileName}${tokenParam}`;
    }

    // Fallback for unexpected formats - ensure forward slashes in URL
    return `${baseUrl}/uploads/${normalizedPath.replace(/^uploads\//, '')}${tokenParam}`;
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{record?._id ? 'Edit Background Check Record' : 'Add New Background Check Record'}</DialogTitle>
      <DialogContent dividers>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
        <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* --- Standard Fields --- */}
            <Grid item xs={12} sm={6}>
              <TextField label="Person's Name" name="personName" value={formData.personName || ''} onChange={handleFormChange} fullWidth required />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField label="Date of Birth" name="dateOfBirth" type="date" value={formData.dateOfBirth?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField label="SSN (Last 4)" name="ssnLast4" value={formData.ssnLast4 || ''} onChange={handleFormChange} fullWidth inputProps={{ maxLength: 4 }} />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField label="Email" name="email" type="email" value={formData.email || ''} onChange={handleFormChange} fullWidth />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField label="Phone" name="phone" type="tel" value={formData.phone || ''} onChange={handleFormChange} fullWidth />
            </Grid>

            {/* --- Address Fields --- */}
             <Grid item xs={12}><Typography variant="subtitle2" sx={{mt:1}}>Address</Typography></Grid>
             <Grid item xs={12}>
                 <TextField label="Street 1" name="address.street1" value={formData.address?.street1 || ''} onChange={handleFormChange} fullWidth />
             </Grid>
             <Grid item xs={12}>
                 <TextField label="Street 2" name="address.street2" value={formData.address?.street2 || ''} onChange={handleFormChange} fullWidth />
             </Grid>
             <Grid item xs={12} sm={4}>
                 <TextField label="City" name="address.city" value={formData.address?.city || ''} onChange={handleFormChange} fullWidth />
             </Grid>
             <Grid item xs={12} sm={3}>
                 <TextField label="State" name="address.state" value={formData.address?.state || ''} onChange={handleFormChange} fullWidth />
             </Grid>
             <Grid item xs={12} sm={3}>
                 <TextField label="Zip Code" name="address.zip" value={formData.address?.zip || ''} onChange={handleFormChange} fullWidth />
             </Grid>
             <Grid item xs={12} sm={2}>
                 <TextField label="Country" name="address.country" value={formData.address?.country || 'US'} onChange={handleFormChange} fullWidth />
             </Grid>
             {/* --- End Address Fields --- */}

             {/* --- Assigned Roles --- */}
             <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                   <Typography variant="subtitle2" sx={{mt:1}}>Assigned Roles (for User Creation)</Typography>

                   {/* Original Role Button - Always show if originalRole exists */}
                   {formData.assignedRoles?.originalRole && (
                      <Button
                         variant="outlined"
                         size="small"
                         color="primary"
                         sx={{ ml: 2, mt: 1, textTransform: 'none' }}
                         startIcon={<InfoIcon fontSize="small" />}
                      >
                         Original Role: {formData.assignedRoles.originalRole}
                      </Button>
                   )}
                </Box>
                <FormGroup row>
                    {ROLE_OPTIONS.map(role => (
                         <FormControlLabel
                            key={role}
                            control={
                                <Checkbox
                                    checked={formData.assignedRoles?.[role] || false}
                                    onChange={handleFormChange}
                                    name={`assignedRoles.${role}`}
                                    size="small"
                                />
                            }
                            label={role.charAt(0).toUpperCase() + role.slice(1)} // Capitalize
                         />
                    ))}
                     <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.assignedRoles?.other || false}
                                onChange={handleFormChange}
                                name="assignedRoles.other"
                                size="small"
                            />
                        }
                        label="Other"
                     />
                </FormGroup>
                {formData.assignedRoles?.other && (
                     <TextField
                        label="Other Role Name"
                        name="assignedRoles.otherRoleName"
                        value={formData.assignedRoles?.otherRoleName || ''}
                        onChange={handleFormChange}
                        fullWidth
                        size="small"
                        sx={{mt: 1}}
                     />
                )}
             </Grid>
             {/* --- End Assigned Roles --- */}


             <Grid item xs={12} sm={6}>
               <FormControl fullWidth>
                 <InputLabel>Check Type</InputLabel>
                 <Select label="Check Type" name="checkType" value={formData.checkType || 'Comprehensive'} onChange={handleFormChange}>
                   {CHECK_TYPE_OPTIONS.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                 </Select>
               </FormControl>
            </Grid>
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth>
                 <InputLabel>Status</InputLabel>
                 <Select label="Status" name="status" value={formData.status || 'Requested'} onChange={handleFormChange}>
                   {STATUS_OPTIONS.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                 </Select>
               </FormControl>
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField label="Date Requested" name="dateRequested" type="date" value={formData.dateRequested?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField label="Date Completed" name="dateCompleted" type="date" value={formData.dateCompleted?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField label="Date Expires" name="dateExpires" type="date" value={formData.dateExpires?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField label="Date Reviewed" name="dateReviewed" type="date" value={formData.dateReviewed?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField label="Provider" name="provider" value={formData.provider || ''} onChange={handleFormChange} fullWidth />
            </Grid>
             <Grid item xs={12}>
              <TextField label="Review Notes" name="reviewNotes" value={formData.reviewNotes || ''} onChange={handleFormChange} fullWidth multiline rows={3} />
            </Grid>
             <Grid item xs={12}>
              <TextField label="MVR Status/Notes" name="mvrStatus" value={formData.mvrStatus || ''} onChange={handleFormChange} fullWidth multiline rows={2} />
            </Grid>
             <Grid item xs={12}>
              <TextField label="Criminal Status/Notes" name="criminalStatus" value={formData.criminalStatus || ''} onChange={handleFormChange} fullWidth multiline rows={2} />
            </Grid>

            {/* --- Criminal Record Locations Section --- */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Criminal Record Locations</Typography>
              <List dense>
                {(formData.criminalRecordLocations || []).map((loc, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete location" onClick={() => handleRemoveLocation(index)}>
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={`${loc.city || 'N/A'}, ${loc.countyOrParish || 'N/A'}, ${loc.state || 'N/A'}`} />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                <TextField label="City" name="city" value={newLocation.city} onChange={handleNewLocationChange} size="small" sx={{ flexGrow: 1 }}/>
                <TextField label="County/Parish" name="countyOrParish" value={newLocation.countyOrParish} onChange={handleNewLocationChange} size="small" sx={{ flexGrow: 1 }}/>
                <TextField label="State" name="state" value={newLocation.state} onChange={handleNewLocationChange} size="small" sx={{ width: '80px' }}/>
                <Button onClick={handleAddLocation} variant="outlined" size="small" sx={{ height: '40px' }}>Add Loc</Button>
              </Box>
            </Grid>
            {/* --- End Criminal Record Locations Section --- */}

            <Grid item xs={12}>
              <TextField label="Other Details" name="otherDetails" value={formData.otherDetails || ''} onChange={handleFormChange} fullWidth multiline rows={2} />
            </Grid>

            {/* File Upload/Link */}
             <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Document</Typography>
                {formData.documentPath && (
                    <Box mb={1}>
                        <Link href={getDocumentUrl(formData.documentPath)} target="_blank" rel="noopener noreferrer">
                            View Current Document ({formData.documentPath.split('/').pop()})
                        </Link>
                    </Box>
                )}
                <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                >
                    {fileToUpload ? fileToUpload.name : (formData.documentPath ? 'Replace Document' : 'Upload Document')}
                    <input
                        type="file"
                        hidden
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                </Button>
             </Grid>

          </Grid>
      </DialogContent>
      <DialogActions>
        {record?._id && (
            <Button onClick={handleDeleteClick} variant="outlined" color="error" startIcon={<DeleteIcon />}>
                Delete Record
            </Button>
        )}
        <Button onClick={onClose} variant="outlined" startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" startIcon={<SaveIcon />}>
          {record?._id ? 'Save Changes' : 'Add Record'}
        </Button>
      </DialogActions>
      {/* Snackbar for notifications */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Background Check"
        content="Are you sure you want to delete this background check record? This action cannot be undone."
        confirmColor="error"
      />
    </Dialog>
  );
}

export default EditBackgroundCheckDialog;